import { InfoOutlined, KeyboardArrowDown, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Input,
  InputAdornment,
  Paper,
  Popover,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import Empty from 'src/components/Empty';
import useEcommerce from 'src/services/useEcommerce';
import { INamespace } from 'src/models/comparison';
import useQueryUrl from 'src/hooks/useQuery';
import { ECOMMERCE_COMPARISON } from 'src/route';
import { useNavigate } from 'react-router';
import {
  initialState,
  reducerUpdateAdvanceClear
} from 'src/features/advanceSearchEcommerce';
import TooltipCustomize from 'src/components/TooltipCustomize';

export default function SelectComparison() {
  const theme = useTheme();
  const projectId = useAppSelector((state) => state.users.project.id);
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const query = useQueryUrl();
  const comparisonId = query.get('id');
  const title = query.get('title');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { useGetEcommerceComparisonList } = useEcommerce();
  const [getComparisonList, { data: comparisonList, loading: isLoading }] =
    useGetEcommerceComparisonList();

  const [selectedComparison, setSelectedComparison] =
    useState<INamespace>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleChangeComparison = (comparison: INamespace, checked: boolean) => {
    if (checked) {
      setSelectedComparison(comparison);
    } else {
      setSelectedComparison(null);
    }
  };

  const renderDataSource = (
    sourceType: 'object' | 'label' | 'objectGroup' | 'productGroup'
  ) => {
    if (sourceType === 'object') return 'Store';
    if (sourceType === 'objectGroup') return 'Group Store';
    if (sourceType === 'label') return 'Label';
    if (sourceType === 'productGroup') return 'Brand or Sub-brand';
  };

  const handleApply = () => {
    dispatch(reducerUpdateAdvanceClear(initialState));
    navigate(
      `${ECOMMERCE_COMPARISON}?tab=dashboard&title=${selectedComparison.displayName}&id=${selectedComparison.id}&sourceType=${selectedComparison.sourceType}`
    );
    handleClose();
  };

  const addName = useMemo((): Array<INamespace & { name: string }> => {
    if (!comparisonList?.ecommerceComparison_getComparisonNamespaceList)
      return [];
    return comparisonList?.ecommerceComparison_getComparisonNamespaceList?.map(
      (comparison) => ({
        ...comparison,
        name: comparison.displayName
      })
    );
  }, [comparisonList]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<INamespace>(addName);

  const filteredList = useMemo(() => {
    if (!selectedComparison) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const activeIdsSet = new Set([selectedComparison.id]);
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList, selectedComparison]);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<INamespace>(filteredList);

  useEffect(() => {
    getComparisonList({ variables: { projectId } });
  }, [projectId]);

  useEffect(() => {
    if (!comparisonList || !comparisonId) return;
    const currentComparison =
      comparisonList?.ecommerceComparison_getComparisonNamespaceList?.find(
        (item) => item.id === comparisonId
      );
    setSelectedComparison(currentComparison);
  }, [comparisonList, comparisonId]);

  return (
    <>
      <Stack width="100%" direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5" fontWeight={600}>
          Select Comparison
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            maxWidth: '400px',
            width: '100%',
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 0.5,
              // maxWidth: '320px',
              // maxWidth: '500px',
              width: '100%',
              overflow: 'scroll',
              scrollbarWidth: 'none'
            }}
          >
            <Chip
              variant="outlined"
              sx={{
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }
              }}
              label={title}
              size="small"
            />
          </Box>
        </Button>
        <TooltipCustomize
          title={
            'Comparison analysis between each object, group of object or label'
          }
          placement="top-start"
        >
          <InfoOutlined fontSize="small" />
        </TooltipCustomize>
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Typography variant="h4">Comparison List</Typography>
          <Box
            sx={{ mt: theme.spacing(1), maxHeight: '300px', overflow: 'auto' }}
          >
            <>
              <Input
                startAdornment={
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                }
                fullWidth
                placeholder="Search Comparison"
                onChange={(event) => handleFilter(event.target.value)}
              />
              <InfiniteScroll
                dataLength={infiniteScroll.list.length}
                next={fetchMoreData}
                hasMore={infiniteScroll.hasMore}
                loader={<div />}
                height={250}
                endMessage={<div />}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    mt: theme.spacing(2),
                    maxHeight: 480
                  }}
                >
                  <Table stickyHeader>
                    <TableHead
                      sx={{
                        background: theme.palette.primary.main
                      }}
                    >
                      <TableRow>
                        <TableCell sx={{ width: '3%' }}></TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Data Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow hover={false}>
                          <TableCell align="center" colSpan={4}>
                            <Box
                              sx={{
                                height: 300,
                                background: theme.palette.grey[200],
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: theme.palette.grey[200],
                                opacity: '0.3'
                              }}
                            >
                              <CircularProgress
                                disableShrink
                                color="primary"
                                size={64}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : comparisonList
                          ?.ecommerceComparison_getComparisonNamespaceList
                          ?.length < 0 ? (
                        <TableRow hover={false}>
                          <TableCell align="center" colSpan={4}>
                            <Box
                              sx={{
                                height: 300,
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <Empty message="There's no data yet" />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        infiniteScroll.list.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0
                              }
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ width: '3%' }}>
                              <Stack direction="row" gap={1}>
                                <Radio
                                  color="primary"
                                  checked={selectedComparison.id === row.id}
                                  onChange={(event) =>
                                    handleChangeComparison(
                                      row,
                                      event.target.checked
                                    )
                                  }
                                />
                              </Stack>
                            </TableCell>
                            <TableCell padding="none" sx={{ flex: 1 }}>
                              {row.displayName}
                            </TableCell>
                            <TableCell padding="none" sx={{ flex: 1 }}>
                              {renderDataSource(row.sourceType)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfiniteScroll>
            </>
          </Box>
          <Stack pt={2} alignItems="end">
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
